import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';
import { ToastItem } from '../components/ui';

interface ToastState {
  message: string;
  kind: 'success' | 'error';
}

interface ActiveToast extends ToastState {
  id: number;
}

/** Never show more than this many toasts at once (oldest is dropped). */
const MAX_STACK = 2;

const ToastContext = createContext<(t: ToastState) => void>(() => {});

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ActiveToast[]>([]);
  const nextId = useRef(0);

  const show = useCallback((t: ToastState) => {
    const id = nextId.current++;
    setToasts((prev) => [...prev, { ...t, id }].slice(-MAX_STACK));
  }, []);

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((x) => x.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={show}>
      {children}
      {toasts.length > 0 && (
        <div className="pointer-events-none fixed bottom-6 right-4 z-[60] flex flex-col items-end gap-2 sm:right-6">
          {toasts.map((t) => (
            <ToastItem key={t.id} message={t.message} kind={t.kind} onDone={() => dismiss(t.id)} />
          ))}
        </div>
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
